import React, { useState, useEffect } from "react"
import NavBar from "@/components/NavBar"
import Footer from "@/components/Footer"
import { initDocsBot } from "@/utils/docsbot"

import { useI18next } from "@zestia/gatsby-plugin-react-i18next"

import "@/styles/main.scss"
import {
  setGclidCookie,
  setReferrerCookie,
  setSourceCookie,
  getCookieForRegion,
  setRegionCookie,
} from "@/utils/cookie-detection"

import { getRegionForCountry } from "@/utils/cookie-detection"
import { RegionContext } from "@/context/region"
import { ScrollSpyContext } from "@/context/scroll-spy"

const MainLayout = ({
  className = "",
  children,
  showNav = true,
  showFooter = true,
  showLogo = true,
  showNavLinks = true,
  showLanguageSwitcher = true,
  showLogIn = true,
  showSignUp = true,
  darkNav = false,
  isSpecialOffer = false,
  darkFooter = false,
  transparentNav = false,
  availableSlugs,
  navBarClassName,
  buttonText,
  hideFooterLinks = false,
  trackingData = {},
}) => {
  const { language, originalPath } = useI18next()

  const [region, setRegion] = useState("US")
  const [activeScrollSpyElement, setActiveScrollSpyElement] = useState("")
  const [cookieConsent, setCookieConsent] = useState(false)

  // Set context, and set cookie based on region passed
  const updateRegion = region => {
    setRegion(region)
    setRegionCookie(region)
    detectCookies(region)
  }

  // Detect region using geo-lookup endpoint
  const fetchRegionForCountry = async () => {
    try {
      // The geo-lookup endpoint is not available in development
      const response = await fetch("/geo-lookup", {
        method: "GET",
        credentials: "same-origin",
      })

      const data = await response.json()
      const country = data["cloudfront-viewer-country"]

      if (country) {
        const detectedRegion = await getRegionForCountry(country)

        return detectedRegion
      }
    } catch (error) {
      console.error("Error fetching country:", error)

      return "US"
    }
  }

  // On mount, detect region and set cookies if necessary
  useEffect(() => {
    const handleRegionDetection = async () => {
      const regionFromCookie = getCookieForRegion()

      // If exp_region cookie exists, use that as the region, else run the above function
      if (regionFromCookie) {
        updateRegion(regionFromCookie)
      } else {
        const detectedRegion = await fetchRegionForCountry()

        updateRegion(detectedRegion)
      }
    }

    handleRegionDetection()
  }, [])

  // Listen for cookie consent updates
  useEffect(() => {
    const handleConsentUpdate = event => {
      if (event.detail.accepted.includes("analytics")) {
        setCookieConsent(true)
        detectCookies()
      }
    }

    document.addEventListener("cookieyes_consent_update", handleConsentUpdate)

    return () => {
      document.removeEventListener(
        "cookieyes_consent_update",
        handleConsentUpdate,
      )
    }
  }, [region])

  const detectCookies = region => {
    const isEuropean = region === "EU" || region === "GB"

    if (isEuropean) {
      if (cookieConsent) {
        // If consent was given, detect cookies
        setSourceCookie()
        setReferrerCookie()
      }
    } else {
      // Non european regions do not need consent to detect cookies
      setSourceCookie()
      setReferrerCookie()
    }

    // Always detect Google Click Identifier (GCLID) regardless of region - this is a necessary cookie
    setGclidCookie()
  }

  // Create virtual page view event with our content data
  // This pushes an event containing data about the current page to the dataLayer
  useEffect(() => {
    let data = {}

    data.intent = trackingData.intent || "none"
    data.language = language
    data.content_group = trackingData.content_category || "other"

    // If window exists, send the page view event
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: "content_view",
        ...data,
      })
    }
  }, [])

  useEffect(() => {
    if (window.location.pathname === "/signup/") {
      const cleanup = initDocsBot()
      return cleanup
    }
  }, [])

  return (
    <ScrollSpyContext.Provider
      value={{
        activeScrollSpyElement,
        setActiveScrollSpyElement,
      }}
    >
      <RegionContext.Provider value={{ region, setRegion: updateRegion }}>
        {showNav && (
          <NavBar
            showLogo={showLogo}
            showNavLinks={showNavLinks}
            showLanguageSwitcher={showLanguageSwitcher}
            showLogIn={showLogIn}
            showSignUp={showSignUp}
            buttonText={buttonText}
            isSpecialOffer={isSpecialOffer}
            darkNav={darkNav}
            transparentNav={transparentNav}
            availableSlugs={availableSlugs}
            className={navBarClassName}
          />
        )}

        <div
          className={`${
            !showNav ? "main-layout--hidden-nav" : ""
          } ${className}`}
        >
          <main>{children}</main>
        </div>

        {showFooter && (
          <Footer darkFooter={darkFooter} hideFooterLinks={hideFooterLinks} />
        )}
      </RegionContext.Provider>
    </ScrollSpyContext.Provider>
  )
}

export default MainLayout
